/**
 * ⌘K / Ctrl+K command palette. Reads whatever the current page registered
 * (see stores/commands) plus the always-on navigation set, ranks contextually
 * — recency and priority (things on screen) float up — and runs the pick.
 * Full-screen sheet on phones (reachable from the menu), centered dialog on md+.
 */
import { cn } from '@/lib/utils';
import { type PaletteCommand, allCommands, useCommandsStore } from '@/stores/commands';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import { CornerDownLeft, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

/** 0 = no match; otherwise 0..1 with prefix > word-start > substring > subsequence. */
function matchScore(text: string, query: string): number {
  const haystack = text.toLowerCase();
  if (haystack.startsWith(query)) {
    return 1;
  }
  if (haystack.split(/\s+/).some((word) => word.startsWith(query))) {
    return 0.85;
  }
  if (haystack.includes(query)) {
    return 0.7;
  }
  // Subsequence: every query char appears in order.
  let at = 0;
  for (const ch of query) {
    at = haystack.indexOf(ch, at);
    if (at === -1) {
      return 0;
    }
    at += 1;
  }
  return 0.35;
}

export function CommandPalette() {
  const open = useCommandsStore((state) => state.open);
  const setOpen = useCommandsStore((state) => state.setOpen);
  const sources = useCommandsStore((state) => state.sources);
  const recent = useCommandsStore((state) => state.recent);
  const markUsed = useCommandsStore((state) => state.markUsed);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  // ⌘K / Ctrl+K anywhere (even inside a field — it's the one global shortcut).
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && (event.key === 'k' || event.key === 'K')) {
        event.preventDefault();
        setOpen(!useCommandsStore.getState().open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setOpen]);

  const commands = useMemo(() => allCommands(sources), [sources]);
  const byId = useMemo(() => new Map(commands.map((command) => [command.id, command])), [commands]);
  const recentRank = useMemo(() => new Map(recent.map((id, index) => [id, index])), [recent]);

  // Render order for the empty query: Recent, then the page's action groups,
  // then Lists and Items (on-screen first; Items capped so the resting view
  // stays short — typing searches everything). cmdk keeps render order when
  // there's no search text and sorts by score once there is.
  const groups = useMemo(() => {
    const recentCommands = recent
      .map((id) => byId.get(id))
      .filter((command): command is PaletteCommand => command !== undefined)
      .slice(0, 5);
    const map = new Map<string, PaletteCommand[]>();
    for (const command of commands) {
      map.set(command.group, [...(map.get(command.group) ?? []), command]);
    }
    const rank = (group: string) =>
      ({ 'This trip': 0, Mode: 1, Filter: 2, Lists: 3, Items: 4, 'Go to': 5, App: 6 })[group] ??
      3.5;
    // At rest, Items are capped at 8 (on-screen ones first); typing searches all.
    const ordered = [...map.entries()]
      .map(([group, list]) => ({
        group,
        list: [...list].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0)),
        restingCap: group === 'Items' ? 8 : Number.POSITIVE_INFINITY,
      }))
      .sort((a, b) => rank(a.group) - rank(b.group));
    return { recentCommands, ordered };
  }, [commands, recent, byId]);

  // Ranking is ours, not cmdk's (shouldFilter=false): score by label/keywords,
  // scale by priority (on-screen things) and recency, order groups by their
  // best hit. Predictable, and the DOM never has to be reshuffled.
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return null;
    }
    const scored = commands
      .map((command) => {
        const base = Math.max(
          matchScore(command.label, q),
          ...(command.keywords ?? []).map((keyword) => matchScore(keyword, q)),
        );
        const recency = recentRank.has(command.id) ? 1 : 0;
        return { command, score: base * (1 + 0.12 * (command.priority ?? 0) + 0.1 * recency) };
      })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score);
    const map = new Map<string, { group: string; list: PaletteCommand[]; best: number }>();
    for (const { command, score } of scored) {
      const entry = map.get(command.group) ?? { group: command.group, list: [], best: 0 };
      entry.list.push(command);
      entry.best = Math.max(entry.best, score);
      map.set(command.group, entry);
    }
    return [...map.values()].sort((a, b) => b.best - a.best);
  }, [commands, query, recentRank]);

  function run(command: PaletteCommand) {
    setOpen(false);
    markUsed(command.id);
    // Let the dialog close before the command opens something else.
    requestAnimationFrame(() => command.run());
  }

  function renderItem(command: PaletteCommand) {
    const Icon = command.icon;
    return (
      <Command.Item
        key={command.id}
        value={command.id}
        keywords={[command.label, ...(command.keywords ?? [])]}
        onSelect={() => run(command)}
        className="flex min-h-11 cursor-pointer select-none items-center gap-3 rounded-lg px-3 text-sm outline-none data-[selected=true]:bg-[var(--accent)] data-[selected=true]:text-[var(--accent-foreground)] md:min-h-9"
      >
        {Icon ? (
          <Icon className="h-4 w-4 shrink-0 text-[var(--muted-foreground)]" />
        ) : (
          <span className="h-4 w-4 shrink-0" />
        )}
        <span className="min-w-0 flex-1 truncate">{command.label}</span>
        {command.hint ? (
          <kbd className="shrink-0 rounded border border-[var(--border)] bg-[var(--muted)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--muted-foreground)]">
            {command.hint}
          </kbd>
        ) : null}
      </Command.Item>
    );
  }

  const groupClass =
    '[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-[var(--muted-foreground)]';

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            'fixed z-50 flex flex-col overflow-hidden bg-[var(--card)] shadow-2xl outline-none',
            // Phone: full-height sheet from the top. md+: centered card.
            'inset-x-0 top-0 h-[100dvh] md:inset-x-auto md:top-[12vh] md:left-1/2 md:h-auto md:max-h-[70vh] md:w-[40rem] md:max-w-[calc(100vw-2rem)] md:-translate-x-1/2 md:rounded-2xl md:border md:border-[var(--border)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 md:data-[state=open]:zoom-in-95',
          )}
        >
          <DialogPrimitive.Title className="sr-only">Commands</DialogPrimitive.Title>
          <Command
            loop
            shouldFilter={false}
            className="flex min-h-0 flex-1 flex-col"
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                setOpen(false);
              }
            }}
          >
            <div className="flex items-center gap-2 border-[var(--border)] border-b px-4">
              <Search className="h-4 w-4 shrink-0 text-[var(--muted-foreground)]" />
              <Command.Input
                value={query}
                onValueChange={setQuery}
                autoFocus
                placeholder="Type a command, a list, or an item…"
                className="h-14 min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-[var(--muted-foreground)]"
              />
              <kbd className="hidden rounded border border-[var(--border)] bg-[var(--muted)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--muted-foreground)] md:inline">
                esc
              </kbd>
            </div>
            <Command.List className={cn('min-h-0 flex-1 overflow-y-auto p-2', groupClass)}>
              {results && results.length === 0 ? (
                <p className="px-3 py-8 text-center text-[var(--muted-foreground)] text-sm">
                  Nothing matches.
                </p>
              ) : null}
              {!query && groups.recentCommands.length > 0 ? (
                <Command.Group heading="Recent">
                  {groups.recentCommands.map((command) => renderItem(command))}
                </Command.Group>
              ) : null}
              {results
                ? results.map(({ group, list }) => (
                    <Command.Group key={group} heading={group}>
                      {list.map((command) => renderItem(command))}
                    </Command.Group>
                  ))
                : groups.ordered.map(({ group, list, restingCap }) => (
                    <Command.Group key={group} heading={group}>
                      {list.slice(0, restingCap).map((command) => renderItem(command))}
                    </Command.Group>
                  ))}
            </Command.List>
            <div className="hidden items-center gap-3 border-[var(--border)] border-t px-4 py-2 text-[11px] text-[var(--muted-foreground)] md:flex">
              <span className="inline-flex items-center gap-1">
                <CornerDownLeft className="h-3 w-3" /> run
              </span>
              <span>↑↓ move</span>
              <span className="ml-auto">⌘K to toggle</span>
            </div>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
